import { useTranslation } from 'react-i18next';
import type { HandoffStatus } from '@/types/api';
import { msg, useMsg, type Msg } from '@/i18n/msg';

type ConversationMode = 'ai' | 'human_handoff' | 'ai_resumed' | 'crisis_paused';

interface HandoffStatusPillProps {
  mode: ConversationMode;
  status?: HandoffStatus;
  expectedResponseAt?: string | null;
  trferName?: string;
  /**
   * When set on a `sla_breached` pill, renders a small "Dismiss" affordance.
   * Clicking calls back so the parent can hide the pill (and any associated
   * system message) without waiting for a reload or a TRFer join.
   */
  onDismissSla?: () => void;
}

/** #2109 R3 L5b: the ETA is a descriptor (count keys), resolved at render. */
function etaMsg(iso?: string | null): Msg {
  if (!iso) return msg('chat:handoff.eta.soon');
  const ms = new Date(iso).getTime() - Date.now();
  const minutes = Math.max(0, Math.round(ms / 60_000));
  if (minutes < 1) return msg('chat:handoff.eta.anyMoment');
  if (minutes < 60) return msg('chat:handoff.eta.minutes', { count: minutes });
  const hours = Math.round(minutes / 60);
  return msg('chat:handoff.eta.hours', { count: hours });
}

export function HandoffStatusPill({
  mode,
  status,
  expectedResponseAt,
  trferName,
  onDismissSla,
}: HandoffStatusPillProps) {
  const { t } = useTranslation('chat');
  const resolve = useMsg();
  if (mode === 'ai' || mode === 'ai_resumed' || mode === 'crisis_paused') return null;

  if (status === 'sla_breached') {
    return (
      <div className="flex justify-center my-2">
        <span className="inline-flex items-center gap-2 text-caption text-status-warn-strong-fg bg-status-warn-strong border border-status-warn-strong-border rounded-full px-3 py-1 font-medium">
          {t('handoff.pill.slaBreached')}
          {onDismissSla && (
            <button
              type="button"
              aria-label={t('handoff.pill.dismissAlert')}
              onClick={onDismissSla}
              className="text-status-warn-fg hover:brightness-110 font-bold leading-none px-1"
            >
              ×
            </button>
          )}
        </span>
      </div>
    );
  }

  // `claimed` covers both "claimed" and "awaiting_family" — once a TRFer
  // takes the thread, the family-side surface is the same regardless of
  // the awaiting flag, so we only need to know that the conversation is
  // no longer in the open queue. Render with the TRFer's name when we
  // have it, fall back to a neutral phrase when we don't.
  if (status === 'claimed' || status === 'awaiting_family') {
    return (
      <div className="flex justify-center my-2">
        <span className="text-caption text-status-warn-fg bg-status-warn border border-status-warn-border rounded-full px-3 py-1 font-mono">
          {trferName ? t('handoff.pill.claimedNamed', { name: trferName }) : t('handoff.pill.claimed')}
        </span>
      </div>
    );
  }

  return (
    <div className="flex justify-center my-2">
      <span className="text-caption text-status-warn-fg bg-status-warn border border-status-warn-border rounded-full px-3 py-1 font-mono">
        {t('handoff.pill.waiting', { eta: resolve(etaMsg(expectedResponseAt)) })}
      </span>
    </div>
  );
}
